import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  categoriesApi,
  itemsApi,
  linksApi,
  locationsApi,
  mediaApi,
  mediaUrl,
  type Item,
  type ItemMedia,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../lib/permissions';
import { formatPrice, formatQuantity } from '../lib/format';
import { FIELD_GROUPS, type FieldName } from '../lib/field-groups';
import ImageCarousel from '../components/ImageCarousel';

export default function ItemDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const writeAccess = canWrite(user?.role);
  const itemId = id ? Number(id) : null;

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => itemsApi.get(itemId!),
    enabled: itemId !== null && Number.isInteger(itemId),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.list,
  });
  const { data: media = [] } = useQuery({
    queryKey: ['item-media', itemId],
    queryFn: () => mediaApi.list(itemId!),
    enabled: itemId !== null,
  });
  const { data: links = [] } = useQuery({
    queryKey: ['item-links', itemId],
    queryFn: () => linksApi.list(itemId!),
    enabled: itemId !== null,
  });

  const removeMutation = useMutation({
    mutationFn: () => itemsApi.remove(itemId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      navigate('/items');
    },
  });

  if (isLoading) {
    return <div className="text-center text-slate-500 py-8">{t('common.loading')}</div>;
  }

  if (!item) {
    return (
      <div className="text-center py-8 space-y-3">
        <p className="text-slate-500">{t('error.ITEM_NOT_FOUND')}</p>
        <Link to="/items" className="text-slate-700 hover:underline">
          {t('items.backToList')}
        </Link>
      </div>
    );
  }

  const category = item.categoryId ? categories.find((c) => c.id === item.categoryId) : null;
  const location = item.locationId ? locations.find((l) => l.id === item.locationId) : null;
  const photos = media.filter((m) => m.kind === 'photo');
  const receipts = media.filter((m) => m.kind === 'receipt');
  const enabled = (item.enabledFields ?? []) as FieldName[];
  const hasField = (f: FieldName) => enabled.includes(f) || hasValue(item, f);

  const qtyNum = Number(item.quantity);
  const lowStock =
    item.lowStockThreshold && qtyNum <= Number(item.lowStockThreshold);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Link to="/items" className="text-sm text-slate-500 hover:text-slate-900 hover:underline">
            ← {t('items.backToList')}
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">{item.name}</h1>
          {item.description && (
            <p className="text-slate-600 mt-1 whitespace-pre-wrap">{item.description}</p>
          )}
        </div>
        {writeAccess && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to={`/items/${item.id}/edit`}
              className="bg-slate-900 text-white rounded px-4 py-2 text-sm font-medium hover:bg-slate-800"
            >
              {t('common.edit')}
            </Link>
            <button
              onClick={() => {
                if (confirm(t('items.confirmDelete', { name: item.name }))) {
                  removeMutation.mutate();
                }
              }}
              className="text-sm text-red-600 hover:text-red-800 hover:underline px-2"
            >
              {t('common.delete')}
            </button>
          </div>
        )}
      </div>

      {/* Hovedbillede + thumbnails */}
      {photos.length > 0 && <PhotoGallery photos={photos} />}

      {/* Stat-grid: kategori, placering, antal, pris */}
      <div className="bg-white rounded-lg shadow p-6">
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-slate-500">{t('field.category')}</dt>
            <dd className="text-slate-900 mt-0.5">
              {category ? (
                <span className="inline-flex items-center gap-1.5">
                  {category.color && (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                  )}
                  {category.name}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">{t('field.location')}</dt>
            <dd className="text-slate-900 mt-0.5">
              {location ? location.path : <span className="text-slate-400">—</span>}
            </dd>
          </div>
          {hasField('quantity') && (
            <div>
              <dt className="text-slate-500">{t('field.quantity')}</dt>
              <dd className="mt-0.5">
                <span className={lowStock ? 'text-red-600 font-medium' : 'text-slate-900'}>
                  {formatQuantity(item.quantity)} {item.unit}
                </span>
                {lowStock && (
                  <span className="ml-2 text-xs text-red-600">
                    ⚠ {t('items.lowStock')}
                  </span>
                )}
              </dd>
            </div>
          )}
          {hasField('priceCents') && item.priceCents !== null && (
            <div>
              <dt className="text-slate-500">{t('field.price')}</dt>
              <dd className="text-slate-900 mt-0.5">
                {formatPrice(item.priceCents, item.currency)}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Per gruppe-card med valgte felter */}
      {FIELD_GROUPS.filter((g) => g.code !== 'inventory').map((group) => {
        const fields = group.fields.filter((f) => hasField(f) && hasValue(item, f));
        if (fields.length === 0) return null;
        // Filtrér priceCents hvis allerede vist øverst
        const visibleFields = fields.filter((f) => f !== 'priceCents' || group.code !== 'purchase');
        if (visibleFields.length === 0) return null;
        return (
          <div key={group.code} className="bg-white rounded-lg shadow p-6">
            <h2 className="text-sm font-semibold text-slate-700 uppercase mb-3">
              {t(`fieldGroups.${group.code}`)}
            </h2>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {visibleFields.map((f) => (
                <FieldDisplay key={f} item={item} field={f} />
              ))}
            </dl>
          </div>
        );
      })}

      {/* Lavt-lager-grænse */}
      {hasField('lowStockThreshold') && item.lowStockThreshold !== null && (
        <div className="bg-white rounded-lg shadow p-6">
          <dl className="text-sm">
            <dt className="text-slate-500">{t('field.lowStockThreshold')}</dt>
            <dd className="text-slate-900 mt-0.5">
              {formatQuantity(item.lowStockThreshold)} {item.unit}
            </dd>
          </dl>
        </div>
      )}

      {/* Kvitteringer */}
      {receipts.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase mb-3">
            {t('media.receipts')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {receipts.map((r) => (
              <ReceiptTile key={r.id} media={r} />
            ))}
          </div>
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-slate-700 uppercase mb-3">
            {t('links.title')}
          </h2>
          <ul className="space-y-1 text-sm">
            {links.map((l) => (
              <li key={l.id}>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-slate-900 hover:underline"
                >
                  {l.label || l.url}
                </a>
                {l.label && (
                  <span className="text-xs text-slate-400 ml-2 truncate inline-block max-w-md align-middle">
                    {l.url}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PhotoGallery({ photos }: { photos: ItemMedia[] }) {
  if (photos.length === 0) return null;
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <ImageCarousel media={photos} />
    </div>
  );
}

function ReceiptTile({ media }: { media: ItemMedia }) {
  const isPdf = media.mimeType === 'application/pdf';
  return (
    <a
      href={mediaUrl(media)}
      target="_blank"
      rel="noopener noreferrer"
      className="aspect-square bg-slate-100 rounded overflow-hidden hover:opacity-90 block"
    >
      {isPdf ? (
        <div className="flex flex-col items-center justify-center h-full text-slate-700 text-xs p-2">
          <span className="text-2xl">📄</span>
          <span className="mt-1 truncate max-w-full">{media.originalFilename ?? 'PDF'}</span>
        </div>
      ) : (
        <img
          src={mediaUrl(media, { thumbnail: !!media.thumbnailKey })}
          alt=""
          className="w-full h-full object-cover"
        />
      )}
    </a>
  );
}

function hasValue(item: Item, field: FieldName): boolean {
  const v = item[field as keyof Item];
  return v !== null && v !== undefined && v !== '';
}

function FieldDisplay({ item, field }: { item: Item; field: FieldName }) {
  const { t } = useTranslation();
  const value = formatField(item, field, t);
  if (!value) return null;
  return (
    <div>
      <dt className="text-slate-500">{t(`field.${field}`)}</dt>
      <dd className="text-slate-900 mt-0.5 whitespace-pre-wrap">{value}</dd>
    </div>
  );
}

function formatField(item: Item, field: FieldName, t: (k: string) => string): string {
  const raw = item[field as keyof Item];
  if (raw === null || raw === undefined || raw === '') return '';

  switch (field) {
    case 'priceCents':
      return formatPrice(item.priceCents, item.currency);
    case 'insuranceValueCents':
      return formatPrice(item.insuranceValueCents, item.currency);
    case 'acquiredAt':
    case 'warrantyExpiresAt':
      return new Date(raw as string).toLocaleDateString('da-DK');
    case 'condition':
      return t(`condition.${raw as string}`);
    case 'quantity':
    case 'lowStockThreshold':
      return formatQuantity(raw as string);
    default:
      return String(raw);
  }
}
